import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import type { CaseStudyImage } from '../data/amexCaseStudy';
type LightboxProps = {
  images: CaseStudyImage[];
  index: number | null;
  onClose: () => void;
  onChange: (index: number) => void;
};
const ease = [0.23, 1, 0.32, 1] as const;
const controlClass = 'flex h-11 w-11 items-center justify-center rounded-full border border-white/70 text-white transition-colors duration-150 hover:bg-white/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:pointer-events-none disabled:opacity-30';
export function Lightbox({
  images,
  index,
  onClose,
  onChange
}: LightboxProps) {
  const reduceMotion = useReducedMotion();
  const closeRef = useRef<HTMLButtonElement>(null);
  const isOpen = index !== null;
  useEffect(() => {
    if (index === null) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && index < images.length - 1) onChange(index + 1);
      if (e.key === 'ArrowLeft' && index > 0) onChange(index - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, index, images.length, onClose, onChange]);
  const image = index !== null ? images[index] : null;
  return <AnimatePresence data-id="element-83" data-mp="290nxt3brou7w">
      {image && index !== null && <motion.div key="lightbox" role="dialog" aria-modal="true" aria-label={image.alt} className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4 md:p-16" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease
    }} onClick={onClose} data-id="element-84" data-mp="290e9dk6dev4b">
          <button ref={closeRef} type="button" aria-label="Close" className={`absolute right-4 top-4 ${controlClass}`} onClick={onClose} data-id="element-85" data-mp="13zodvikrpiuw">
            <XIcon className="h-5 w-5" data-id="element-86" data-mp="u5fmwzbdxxyu" />
          </button>

          <button type="button" aria-label="Previous screenshot" className={`absolute left-4 top-1/2 hidden -translate-y-1/2 md:flex ${controlClass}`} disabled={index === 0} onClick={e => {
        e.stopPropagation();
        onChange(index - 1);
      }} data-id="element-87" data-mp="gbeq4b3zul14">
            <ChevronLeftIcon className="h-5 w-5" data-id="element-88" data-mp="3tiyjb2kqiwcr" />
          </button>

          <motion.img key={image.src} src={image.src} alt={image.alt} className="max-h-full max-w-full rounded-[5px] object-contain shadow-[0_10px_40px_rgba(0,0,0,0.4)]" initial={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        scale: 0.96
      }} transition={{
        duration: 0.22,
        ease
      }} onClick={e => e.stopPropagation()} data-id="element-89" data-mp="3tkt66mfkgyjh" />

          <button type="button" aria-label="Next screenshot" className={`absolute right-4 top-1/2 hidden -translate-y-1/2 md:flex ${controlClass}`} disabled={index === images.length - 1} onClick={e => {
        e.stopPropagation();
        onChange(index + 1);
      }} data-id="element-90" data-mp="1oy3i15v7d5x">
            <ChevronRightIcon className="h-5 w-5" data-id="element-91" data-mp="6iaps4n2n7vi" />
          </button>

          <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-sm text-white/80" data-id="element-92" data-mp="kgqxnywo74f8">
            {index + 1} / {images.length}
          </p>
        </motion.div>}
    </AnimatePresence>;
}